import { Location } from "@angular/common";
import { Component, OnDestroy } from "@angular/core";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { debounceTime, map, Subscription } from "rxjs";
import { SyndicateRepositoryService } from "../syndicate-repository.service";
import { PaymentService } from "src/app/services/payment.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { IonicModule } from "@ionic/angular";
@Component({
    selector: "app-payment-holiday",
    templateUrl: "./payment-holiday.component.html",
    styleUrls: ["./payment-holiday.component.scss"],
    standalone: true,
    imports: [IonicModule, TranslatePipe],
})
export class PaymentHolidayComponent implements OnDestroy {
  public selectedLanguage = "en";
  public latestOngoingSyndicateName: Data;
  public loader: boolean = true;
  public showPaymentHoliday: boolean;
  public syndicateData: Data;
  private subscription: Subscription;
  constructor(
    public translate: TranslateService,
    private location: Location,
    private router: Router,
    private syndicateService: SyndicateRepositoryService,
    private authService: AuthService,
    private paymentService: PaymentService,
    private mixpanelService: MixpanelService
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );
    this.subscription = this.authService?.latestOngoingSyndicate$?.pipe(debounceTime(1000))?.subscribe({
      next: (data) => {
        this.loader = false;
        if (data) {
          this.showPaymentHoliday = true;
          this.latestOngoingSyndicateName = data?.syndicate_name;
          this.syndicateData = data;
        } else {
          this.showPaymentHoliday = false;
        }
      },
      error: (error) => {
        this.loader = false;
        return error;
      },
    });
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * The function `getRedirectionUrl` returns the previous URL path segment or a default URL if there is
   * an error or no previous URL.
   * @returns The `getRedirectionUrl` function returns a string value, which is either a specific URL
   * based on certain conditions or a default URL "/subscription-settings" if an error occurs or if the
   * conditions are not met.
   */
  getRedirectionUrl(): string {
    const defaultUrl = "/subscription-settings";
    try {
      const previousUrl = document?.referrer;
      const currentUrl = this.router.url;
      if (!previousUrl || previousUrl?.includes(currentUrl)) {
        return defaultUrl;
      }
      const urlObject = new URL(previousUrl);
      const pathParts = urlObject.pathname;
      return pathParts || defaultUrl;
    } catch (error) {
      return defaultUrl;
    }
  }

  // To navigate back to home page
  dismiss() {
  this.router.navigate([this.getRedirectionUrl()], { replaceUrl: true });
  }

  // To navigate to store page
  goToStore() {
    this.router.navigateByUrl("/mainTabs/tabs/store");
  }

  // To take payment holiday
  takePaymentHoliday() {
    this.syndicateService
      .getSyndicateHoliday(this.syndicateData.id ? this.syndicateData.id : this.syndicateData.encrypted_id)
      .subscribe((data) => {
        if (data?.status === "rejected") {
          this.authService.getConstantsMsg(data?.error_code).then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong!",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        }
        if (data?.response?.status === "FAIL") {
          this.authService.getConstantsMsg("1095").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong, please try again later.",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        }
        if (data?.response?.status === "SUCCESS") {
          this.triggerEventOnHolidayActivation(this.syndicateData.id ? this.syndicateData.id : this.syndicateData.encrypted_id);
          this.authService.getConstantsMsg("1121").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Payment Holiday confirmed successfully",
                line_2: "",
              },
              style: {
                icon: "tick circle success.svg",
                type: "Success",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        }
        this.dismiss();
      });
  }

  triggerEventOnHolidayActivation(sid) {
    this.paymentService
        .getSyndicateInfo(sid)
        .pipe(
          map((detail: any) => {
            const syndicateInfo = detail?.data();
            if(syndicateInfo) {
              this.mixpanelService.trackPaymentHoliday(syndicateInfo)
            }
          })
        )?.subscribe();
  }
}
